import {
  Box,
  Chip,
  Skeleton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { HubCard } from "~/components/hub-card";
import { Flex } from "~/components/Flex";
import { useEffect, useRef, useState } from "react";
import { BarchartIcon, FleetIcon, PriorityFindingsIcon } from "~/icons";
import { useSpaceOrWorkspaceRisk } from "~/pages/space/home-dashboard/SpaceOrWorkspaceRiskCard/hooks/useSpaceOrWorkspaceRisk";
import { VALUE_ANGLE_OFFSET } from "~/pages/space/home-dashboard/SpaceOrWorkspaceRiskCard/components/SpaceRiskGaugeChart/utils/spaceRiskGaugeChartUtils";
import SpaceRiskGaugeChart from "~/pages/space/home-dashboard/SpaceOrWorkspaceRiskCard/components/SpaceRiskGaugeChart/SpaceRiskGaugeChart";

const GAUGE_CHART_WIDTH = 320;
const SPACE_RISK_TOOLTIP_TEXT =
  "This score represents the overall risk for this space, from 0 (low risk) to 100 (high risk). It is calculated from all active priority findings on your assets.Fix the highest-priority findings to reduce your score.";

interface SpaceRiskCardProps {
  isLoadingScope: boolean;
  findingsCount: number;
  assetCount: number;
  isSpaceDashboard: boolean;
  scopeMrn: string;
}

const SpaceOrWorkspaceRiskCard = ({
  isLoadingScope,
  findingsCount,
  assetCount,
  isSpaceDashboard,
  scopeMrn,
}: SpaceRiskCardProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(GAUGE_CHART_WIDTH);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(600));
  const { spaceRisk, isLoading: isLoadingSpaceRisk } =
    useSpaceOrWorkspaceRisk(scopeMrn);
  const isLoading = isLoadingSpaceRisk || isLoadingScope;

  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    // Defer initial measurement to after browser paint
    setTimeout(updateSize, 300);

    if (isMobile) {
      setContainerWidth(250);
    }

    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, []);

  const gaugeChartWidth = Math.min(containerWidth, GAUGE_CHART_WIDTH);

  return (
    <HubCard
      {...{
        loading: isLoading,
        title: (
          <Flex alignItems="center">
            <Typography
              variant="h5"
              sx={{
                textTransform: "uppercase",
                mb: -0.5,
                fontWeight: 700,
                mr: 1,
              }}
            >
              {isSpaceDashboard ? "SPACE RISK" : "WORKSPACE RISK"}
            </Typography>
            <Tooltip title={SPACE_RISK_TOOLTIP_TEXT} arrow placement="right">
              <InfoOutlinedIcon sx={{ color: "secondary.main", width: 16 }} />
            </Tooltip>
            <Chip
              label="BETA"
              slotProps={{
                label: { sx: { fontSize: 12, fontWeight: "bold", p: 0, m: 0 } },
              }}
              sx={{
                borderRadius: 2,
                px: 1,
                py: 0.5,
                ml: 1,
              }}
            />
          </Flex>
        ),
        icon: <BarchartIcon />,
        sx: { height: "100%" },
      }}
    >
      <Flex
        ref={containerRef}
        sx={{
          marginLeft: `-${VALUE_ANGLE_OFFSET}px`,
          flexDirection: "column",
          height: "100%",
        }}
      >
        {/* Gauge Chart */}
        <SpaceRiskGaugeChart
          gaugeWidth={gaugeChartWidth}
          riskValue={spaceRisk?.space_risk}
          isSpaceDashboard={isSpaceDashboard}
        />

        {/* Statistics */}
        <Box sx={{ mt: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <PriorityFindingsIcon sx={{ color: "text.secondary" }} />
              <Typography sx={{ color: "text.secondary" }}>
                Priority Findings
              </Typography>
            </Box>
            <Typography
              sx={{ color: "text.secondary", fontWeight: "bold" }}
              id="findings-count"
            >
              {isLoading ? <Skeleton width={50} /> : findingsCount}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <FleetIcon sx={{ color: "text.secondary" }} />
              <Typography sx={{ color: "text.secondary" }}>
                Asset Count
              </Typography>
            </Box>
            <Typography
              sx={{ color: "text.secondary", fontWeight: "bold" }}
              id="asset-count"
            >
              {isLoading ? <Skeleton width={50} /> : assetCount}
            </Typography>
          </Box>
        </Box>
      </Flex>
    </HubCard>
  );
};

export default SpaceOrWorkspaceRiskCard;
